import gql from 'graphql-tag';
import { globalConfig } from 'core/config';
import { analytics } from 'core/analytics';

export function isNotFound(userErr) {
  if (userErr.code === 'NOTFOUND') {
    return true;
  }
  return false;
}

export const USER_ERROR_FIELDS = gql`
  fragment userErrorFields on UserError {
    message
    key
    code
  }
`;

// Used for backend go error codes only
export const ErrorCode = {
  VALIDATION: 0,
  AUTHENTICATION: 1,
  AUTHORISATION: 2,
  NOTFOUND: 3,
  REDIRECT: 4,
  USER_MISMATCH: 5,
  USER_NOT_CONFIRMED: 6,
  PROJECT_PAYMENT_OVERDUE: 7,
  BILLING_CARD_DECLINED: 8,
  BILLING_ERROR: 9,
  INVITE_ERROR: 10,
  UNHANDLED_ERROR: 11,
  NO_BOT: 12,
  BAD_REQUEST: 13,
  INVALID_TOKEN: 14,
};

// Python Backend Error Handling

export const logBadRequestQuery = (error, query) => {
  const scopingFunction = scope => {
    try {
      if (query.appManager && query.appManager.constructor) {
        scope.setExtra(
          'appManager',
          query.appManager.constructor?.registryName ||
            query.appManager.constructor.name,
        );
      }
      if (query.observableQuery && query.observableQuery.variables) {
        scope.setExtra(
          'variables',
          JSON.stringify(query.observableQuery.variables),
        );
      }
    } catch (e) {
      console.error('An error ocurred while handling an error!');
    }
  };
  analytics.logError(error, scopingFunction);
};

export const handleGoErrors = (queryOrMut, err) => {
  /* For authentication errors we really need to do something different depending on whether the user is loggedIn (has jwt)!
  Backend go doesn't really know if the user is logged out or not, just whether the user has a valid JWT.

  If the user receives an authentication error but is logged in we should return a 404 or tell them they don't have access.
 */

  const userErrs = [];
  // if there is any kind of network errors (which includes 400 etc we should ignore graphql errors)
  if (!err?.graphQLErrors) {
    return [err, []];
  }
  err.graphQLErrors.forEach(graphqlErr => {
    if (graphqlErr.extensions?.code !== undefined) {
      if (graphqlErr.extensions.code === ErrorCode.AUTHENTICATION) {
        userErrs.push(graphqlErr);
        if (globalConfig.onAuthenticationError) {
          globalConfig.onAuthenticationError(graphqlErr);
        }
      } else if (graphqlErr.extensions.code === ErrorCode.AUTHORISATION) {
        userErrs.push(graphqlErr);
        queryOrMut.notFound = true; // could be moved to onAuthorisationError
        if (globalConfig.onAuthorisationError) {
          globalConfig.onAuthorisationError(graphqlErr);
        }
      } else if (graphqlErr.extensions.code === ErrorCode.NOTFOUND) {
        // eslint-disable-next-line no-param-reassign
        queryOrMut.notFound = true;
        userErrs.push(graphqlErr);
        if (globalConfig.onNotFoundError) {
          globalConfig.onNotFoundError(graphqlErr);
        }
      } else if (graphqlErr.extensions.code === ErrorCode.VALIDATION) {
        // deal with validationErrors and any other user errors, by adding them to the userErrors array
        userErrs.push(graphqlErr);
      } else if (graphqlErr.extensions.code === ErrorCode.BAD_REQUEST) {
        // this is assumed to be an invalid url
        queryOrMut.notFound = true;
        if (globalConfig?.onNotFoundError) {
          globalConfig.onNotFoundError(graphqlErr);
        }
      } else if (graphqlErr.extensions.code === ErrorCode.UNHANDLED_ERROR) {
        // do nothing this is effectively a sysErr
      }
    }
  });
  // if there are no specific Wikifactory errors codes found then this is a generic sys error
  if (userErrs.length === 0) {
    return [err, []];
  }
  return [null, userErrs];
};

export const handlePythonErrors = queryOrMut => {
  if (queryOrMut.userErrors.length === 0) {
    return;
  }
  queryOrMut.userErrors.forEach(userErr => {
    if (userErr.code === 'AUTHENTICATION') {
      if (globalConfig.onAuthenticationError) {
        globalConfig.onAuthenticationError(userErr);
      }
    } else if (userErr.code === 'USER_MISMATCH') {
      if (globalConfig.onUserMismatchError) {
        globalConfig.onUserMismatchError(userErr);
      }
    } else if (userErr.code === 'AUTHORISATION') {
      // eslint-disable-next-line no-param-reassign
      queryOrMut.notFound = true; // could be moved to onAuthorisationError
      if (globalConfig.onAuthorisationError) {
        globalConfig.onAuthorisationError(userErr);
      }
    } else if (userErr.code === 'REDIRECT') {
      if (globalConfig.onAuthorisationError) {
        globalConfig.onRedirectError(userErr);
      }
    } else if (userErr.code === 'PROJECT_PAYMENT_OVERDUE') {
      if (globalConfig.onPaymentOverdueError) {
        globalConfig.onPaymentOverdueError(userErr);
      }
    } else if (userErr.code === 'NO_BOT') {
      if (globalConfig.onNoBot) {
        globalConfig.onNoBot(userErr);
      }
    } else if (userErr.code === 'NOTFOUND') {
      // eslint-disable-next-line no-param-reassign
      queryOrMut.notFound = true;
      if (globalConfig.onNotFoundError) {
        globalConfig.onNotFoundError(userErr);
      }
    } else if (userErr.code === 'USER_NOT_CONFIRMED') {
      if (globalConfig.onNotConfirmedError) {
        globalConfig.onNotConfirmedError(userErr);
      }
    }
    if (userErr.code === 'PROJECT_PAYMENT_OVERDUE') {
      if (globalConfig.onPaymentOverdueError) {
        globalConfig.onPaymentOverdueError(userErr);
      }
    } else if (userErr.code === 'BAD_REQUEST') {
      if (queryOrMut?.observableQuery) {
        logBadRequestQuery(userErr, queryOrMut);
      } else {
        // it's a mutation
        analytics.logError(userErr);
      }
    }
  });
  if (queryOrMut.onUserErr) {
    queryOrMut.onUserErr(queryOrMut.userErrors);
  }
};
